import React from 'react';
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { CheckCircle, MicOff, MovieFilter, UploadFile } from '@mui/icons-material';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';

const ON_PRIMARY = '#533400';
const SURFACE = '#1a1a1a';
const SURFACE_HIGH = '#262626';
const ON_VARIANT = '#adaaaa';

type EmptyCardProps = {
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    onUpload?: () => void;
    /** Span full column width (e.g. align with primary title card on content creator). */
    fullWidth?: boolean;
    /** Tall 16:9-style placeholder for artist featured reel slot. */
    featuredHero?: boolean;
    /** Someone else’s profile (or signed-out): neutral copy; hide upload CTAs / feature lists (upload still gated by `isEditMode`). */
    visitorView?: boolean;
    /** Minimal placeholder for campaign video picker (no demo reel marketing). */
    emptyStateVariant?: 'profile' | 'campaignVideo';
};

/** Compact empty state for demo reels — matches THE STAGE card body (not page chrome). */
export const DemoReelEmptyCard: React.FC<EmptyCardProps> = ({
    themeStyle,
    isEditMode,
    onUpload,
    fullWidth,
    featuredHero,
    visitorView,
    emptyStateVariant = 'profile',
}) => {
    const h = theme[themeStyle].highlight;

    if (emptyStateVariant === 'campaignVideo') {
        return (
            <Box
                sx={{
                    borderRadius: '10px',
                    border: '1px dashed rgba(72,72,71,0.45)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    py: 3,
                    px: 2,
                    width: fullWidth || featuredHero ? '100%' : undefined,
                    maxWidth: fullWidth || featuredHero ? 'none' : 520,
                    minHeight: featuredHero ? { xs: 160, sm: 200 } : undefined,
                    aspectRatio: featuredHero ? '16/9' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: 14,
                        color: ON_VARIANT,
                        textAlign: 'center',
                        maxWidth: 320,
                        lineHeight: 1.55,
                    }}>
                    Select a video below and it will appear here for use in contest management.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: featuredHero ? '16px' : '14px',
                bgcolor: SURFACE,
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                py: featuredHero ? { xs: 6, sm: 8 } : { xs: 2.75, sm: 3.25 },
                px: { xs: 2, sm: 2.75 },
                width: fullWidth || featuredHero ? '100%' : undefined,
                maxWidth: fullWidth || featuredHero ? 'none' : 520,
                minHeight: featuredHero ? { xs: 220, sm: 300, md: 360 } : undefined,
                aspectRatio: featuredHero ? '16/9' : undefined,
                display: featuredHero ? 'flex' : undefined,
                alignItems: featuredHero ? 'center' : undefined,
                justifyContent: featuredHero ? 'center' : undefined,
                boxSizing: 'border-box',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    opacity: 0.14,
                    background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250,175,63,0.35), transparent 60%)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                {/* Icon + bars grouped; mb 1.5 before title matches audio empty (icon → title gap). */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: SURFACE_HIGH,
                            border: '1px solid rgba(255,255,255,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 0.5,
                        }}>
                        <MovieFilter sx={{ fontSize: 28, color: 'rgba(255,255,255,0.22)' }} />
                    </Box>
                    <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={0.6}>
                        <Box sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: `${h}33` }} />
                        <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: `${h}66` }} />
                        <Box sx={{ width: 4, height: 16, borderRadius: 1, bgcolor: `${h}33` }} />
                    </Stack>
                </Box>
                {visitorView ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: { xs: '1.05rem', sm: '1.15rem' },
                            letterSpacing: '-0.02em',
                            mb: 1,
                            lineHeight: 1.25,
                        }}>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            No{' '}
                        </Box>
                        <Box component="span" sx={{ color: h }}>
                            Demo Reels
                        </Box>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            {' '}
                            Yet.
                        </Box>
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: { xs: '1.05rem', sm: '1.15rem' },
                            letterSpacing: '-0.02em',
                            mb: 1,
                            lineHeight: 1.25,
                        }}>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            No{' '}
                        </Box>
                        <Box component="span" sx={{ color: h }}>
                            Demo Reels
                        </Box>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            {' '}
                            Yet
                        </Box>
                    </Typography>
                )}
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: 12,
                        color: ON_VARIANT,
                        maxWidth: 300,
                        mx: 'auto',
                        lineHeight: 1.55,
                        mb: isEditMode && onUpload ? 2 : 0,
                    }}>
                    {visitorView ? (
                        <>
                            New content will appear here once uploaded.
                            <Box component="br" />
                            Check back later for updates.
                        </>
                    ) : (
                        <>
                            Let your talent shine. Upload your first professional reel to increase your visibility by up
                            to{' '}
                            <Box component="span" sx={{ color: theme[themeStyle].primary_white, fontWeight: 600 }}>
                                85%
                            </Box>
                            .
                        </>
                    )}
                </Typography>
                {isEditMode && onUpload && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<UploadFile sx={{ fontSize: 20 }} />}
                        onClick={onUpload}
                        sx={{
                            bgcolor: h,
                            color: ON_PRIMARY,
                            borderRadius: '999px',
                            py: 1.15,
                            px: 3,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            boxShadow: '0 0 28px rgba(250, 175, 63, 0.2)',
                            '&:hover': {
                                bgcolor: theme[themeStyle].primary_orange,
                                color: ON_PRIMARY,
                                boxShadow: '0 0 36px rgba(250, 175, 63, 0.28)',
                            },
                        }}>
                        Upload Demo Reel
                    </Button>
                )}
                {!visitorView && (
                    <>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mt: 2.5, mb: 1.75 }} />
                        <Grid container spacing={1.5} sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                            <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircle sx={{ fontSize: 18, color: h, flexShrink: 0, mt: 0.1 }} />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                            }}>
                                            4K Supported
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 10, color: ON_VARIANT, mt: 0.35, lineHeight: 1.35 }}>
                                            High-fidelity playback for casting.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircle sx={{ fontSize: 18, color: h, flexShrink: 0, mt: 0.1 }} />
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                            }}>
                                            HLS Supported
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 10, color: ON_VARIANT, mt: 0.35, lineHeight: 1.35 }}>
                                            HTTP-based adaptive bitrate streaming protocol
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>
        </Box>
    );
};

/** Compact empty state for audio reels. Matches `DemoReelEmptyCard` when `featuredHero` / `fullWidth` are set. */
export const AudioReelEmptyCard: React.FC<EmptyCardProps> = ({
    themeStyle,
    isEditMode,
    onUpload,
    fullWidth,
    featuredHero,
    visitorView,
}) => {
    const h = theme[themeStyle].highlight;
    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: featuredHero ? '16px' : '14px',
                bgcolor: SURFACE,
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                py: featuredHero ? { xs: 6, sm: 8 } : { xs: 2.75, sm: 3.25 },
                px: { xs: 2, sm: 2.75 },
                width: fullWidth || featuredHero ? '100%' : undefined,
                maxWidth: fullWidth || featuredHero ? 'none' : 520,
                minHeight: featuredHero ? { xs: 220, sm: 300, md: 360 } : undefined,
                aspectRatio: featuredHero ? '16/9' : undefined,
                display: featuredHero ? 'flex' : undefined,
                alignItems: featuredHero ? 'center' : undefined,
                justifyContent: featuredHero ? 'center' : undefined,
                boxSizing: 'border-box',
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    opacity: featuredHero ? 0.14 : 0.1,
                    background: featuredHero
                        ? 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250,175,63,0.35), transparent 60%)'
                        : 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(250,175,63,0.3), transparent 65%)',
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
                {featuredHero ? (
                    <>
                        {/* Same icon + bars grouping as demo reel empty (featured) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '50%',
                                    bgcolor: SURFACE_HIGH,
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 0.5,
                                }}>
                                <MicOff sx={{ fontSize: 28, color: 'rgba(255,255,255,0.22)' }} />
                            </Box>
                            <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={0.6}>
                                <Box sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: `${h}33` }} />
                                <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: `${h}66` }} />
                                <Box sx={{ width: 4, height: 16, borderRadius: 1, bgcolor: `${h}33` }} />
                            </Stack>
                        </Box>
                    </>
                ) : (
                    <>
                        <Stack
                            direction="row"
                            justifyContent="center"
                            alignItems="flex-end"
                            spacing={0.5}
                            sx={{ mb: 1.25 }}>
                            <Box sx={{ width: 3, height: 16, borderRadius: 0.5, bgcolor: `${h}44` }} />
                            <Box sx={{ width: 3, height: 24, borderRadius: 0.5, bgcolor: `${h}77` }} />
                            <Box sx={{ width: 3, height: 12, borderRadius: 0.5, bgcolor: `${h}33` }} />
                            <Box sx={{ width: 3, height: 20, borderRadius: 0.5, bgcolor: `${h}55` }} />
                        </Stack>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '14px',
                                bgcolor: SURFACE_HIGH,
                                border: '1px solid rgba(255,255,255,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 1.5,
                            }}>
                            <MicOff sx={{ fontSize: 28, color: h }} />
                        </Box>
                    </>
                )}
                {visitorView ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: {
                                xs: featuredHero ? '1.05rem' : '1.02rem',
                                sm: featuredHero ? '1.15rem' : '1.12rem',
                            },
                            letterSpacing: '-0.02em',
                            mb: 1,
                            lineHeight: 1.25,
                        }}>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            No{' '}
                        </Box>
                        <Box component="span" sx={{ color: h }}>
                            Audio Reels
                        </Box>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            {' '}
                            Yet
                        </Box>
                    </Typography>
                ) : (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: {
                                xs: featuredHero ? '1.05rem' : '1.02rem',
                                sm: featuredHero ? '1.15rem' : '1.12rem',
                            },
                            letterSpacing: '-0.02em',
                            mb: 1,
                            lineHeight: 1.25,
                        }}>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            Your{' '}
                        </Box>
                        <Box component="span" sx={{ color: h }}>
                            Audio Gallery{' '}
                        </Box>
                        <Box component="span" sx={{ color: theme[themeStyle].primary_white }}>
                            is{' '}
                        </Box>
                        <Box component="span" sx={{ color: h }}>
                            Silent
                        </Box>
                    </Typography>
                )}
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: 12,
                        color: ON_VARIANT,
                        maxWidth: featuredHero ? 300 : 320,
                        mx: 'auto',
                        lineHeight: 1.55,
                        mb: isEditMode && onUpload ? 2 : 0,
                    }}>
                    {visitorView ? (
                        <>
                            New content will appear here once uploaded.
                            <Box component="br" />
                            Check back later for updates.
                        </>
                    ) : (
                        'Casting directors look for voice quality and range. Upload your vocal reels or performance samples to start making an impact.'
                    )}
                </Typography>
                {isEditMode && onUpload && (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<UploadFile sx={{ fontSize: 20 }} />}
                        onClick={onUpload}
                        sx={{
                            bgcolor: h,
                            color: ON_PRIMARY,
                            borderRadius: '999px',
                            py: 1.15,
                            px: 3,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            boxShadow: '0 0 28px rgba(250, 175, 63, 0.2)',
                            '&:hover': {
                                bgcolor: theme[themeStyle].primary_orange,
                                color: ON_PRIMARY,
                                boxShadow: '0 0 36px rgba(250, 175, 63, 0.28)',
                            },
                        }}>
                        Upload Audio Track
                    </Button>
                )}
                {!visitorView && (
                    <>
                        <Divider
                            sx={{
                                borderColor: 'rgba(255,255,255,0.06)',
                                mt: featuredHero ? 2.5 : 2.25,
                                mb: featuredHero ? 1.75 : 1.5,
                            }}
                        />
                        {featuredHero ? (
                            <Grid container spacing={1.5} sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <CheckCircle sx={{ fontSize: 18, color: h, flexShrink: 0, mt: 0.1 }} />
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                MP3, WAV, AAC
                                            </Typography>
                                            <Typography
                                                sx={{ fontSize: 10, color: ON_VARIANT, mt: 0.35, lineHeight: 1.35 }}>
                                                Industry-standard formats for casting review.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <CheckCircle sx={{ fontSize: 18, color: h, flexShrink: 0, mt: 0.1 }} />
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    color: theme[themeStyle].primary_white,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                MAX 50MB
                                            </Typography>
                                            <Typography
                                                sx={{ fontSize: 10, color: ON_VARIANT, mt: 0.35, lineHeight: 1.35 }}>
                                                Plenty of room for high-quality samples.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        ) : (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                justifyContent="center"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                sx={{ px: { sm: 1 } }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <CheckCircle sx={{ fontSize: 16, color: h }} />
                                    <Typography
                                        sx={{ fontSize: 11, color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        MP3, WAV, AAC
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <CheckCircle sx={{ fontSize: 16, color: h }} />
                                    <Typography
                                        sx={{ fontSize: 11, color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        MAX 50MB
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};
